/**
 * MyProfile Template
 *
 * Pure presentation component for user profile page
 * Displays user information and handles password change/logout
 *
 * REFACTORED with Facade Pattern!
 *
 * Before: 20+ individual props (user, dialogs, handlers...)
 * After: 1 facade + 2 platform-specific props
 *
 * @package @ideoz/ui
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Info, Moon, Sun, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogFooter,
  DialogHeader,
  Button,
  Input,
  Label,
} from '@ideoz/ui';
import { cn } from '../../lib/utils';
import {
  calculatePasswordStrength,
  selectTheme,
  setTheme,
  useAppDispatch,
  useAppSelector,
  type PasswordStrength,
  type MyProfilePageFacade,
} from '@ideoz/core';
import { AppLayout } from '../layouts/AppLayout';
import { Header } from '../organisms/Header';

/**
 * MyProfileTemplate Props
 *
 * SIMPLIFIED with Facade Pattern!
 *
 * Before: 20+ individual props
 * After: 1 facade + 2-3 platform-specific props
 *
 * The facade encapsulates all shared logic following useLandingPageFacade pattern:
 * - State (isLoggedIn, user)
 * - Header configuration & user menu
 * - Navigation (toLanding, toProfile, etc.)
 * - Profile actions (dialogs, change password, etc.)
 * - Logout action
 */
export interface MyProfileTemplateProps {
  // ========== FACADE (all shared logic) ==========
  facade: MyProfilePageFacade;

  // ========== Platform-Specific Props ==========
  isWeb: boolean;

  // Web-specific
  authDialogState?: {
    isOpen: boolean;
    mode: 'login' | 'register';
    openLogin: () => void;
    openRegister: () => void;
    closeDialog: () => void;
  };

  /** Optional language switcher, injected by the host (web owns i18n). */
  languageSwitcher?: React.ReactNode;
}

export default function MyProfileTemplate({
  facade,
  isWeb,
  authDialogState,
  languageSwitcher,
}: MyProfileTemplateProps): JSX.Element {
  const { t } = useTranslation();

  // ========== Destructure Facade ==========
  const {
    state: { user },
    header: {
      headerProps,
      userMenuOpen,
      onUserMenuClose,
      userMenuItems,
      userMenuSocialLinks,
      onRequestUpgrade,
      isRequestingUpgrade,
    },
    navigation,
    profile: { logoutDialog, changePasswordDialog },
    logout: { logout: onLogout },
    subscriptionExpiration: { isExpiringWithin3Days, daysUntilExpiration },
    onRenewSubscription,
  } = facade;

  // Local state for password dialog
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    isValid: false,
    className: '',
    label: 'empty',
  });
  const samePasswordMessage = t('profile.passwordDialog.mustDiffer');
  const isSamePassword = Boolean(oldPassword && newPassword && oldPassword === newPassword);

  // Appearance — dark/light toggle. Persisted via redux-persist; the root <html>
  // class is synced in web/src/App.tsx so Tailwind `dark:` variants activate
  // everywhere without each screen re-implementing the effect.
  const dispatch = useAppDispatch();
  const theme = useAppSelector(selectTheme);
  const isDark = theme === 'dark';
  const toggleTheme = useCallback(() => {
    dispatch(setTheme(isDark ? 'light' : 'dark'));
  }, [dispatch, isDark]);

  // Password visibility toggle
  const togglePassword = useCallback(() => {
    setShowPassword(!showPassword);
  }, [showPassword]);

  // Password strength checker
  const checkPassword = useCallback((password: string) => {
    setPasswordError('');
    const strength = calculatePasswordStrength(password);
    setPasswordStrength(strength);
  }, []);

  // Handle password change submission
  const handlePasswordChange = useCallback(async () => {
    if (!oldPassword) {
      setPasswordError(t('profile.passwordDialog.currentRequired'));
      return;
    }

    if (!newPassword) {
      setPasswordError(t('profile.passwordDialog.newRequired'));
      return;
    }

    if (oldPassword === newPassword) {
      setPasswordError(samePasswordMessage);
      return;
    }

    if (!passwordStrength.isValid) {
      setPasswordError(t('profile.passwordDialog.tooWeak'));
      return;
    }

    try {
      await changePasswordDialog.changePassword(oldPassword, newPassword);
      setOldPassword('');
      setNewPassword('');
      setPasswordError('');
      setShowPassword(false);
    } catch (error) {
      // Error already shown by facade
    }
  }, [
    oldPassword,
    newPassword,
    passwordStrength.isValid,
    changePasswordDialog,
    samePasswordMessage,
    t,
  ]);

  // Handle password dialog close
  const handlePasswordDialogClose = useCallback(() => {
    changePasswordDialog.close();
    setOldPassword('');
    setNewPassword('');
    setPasswordError('');
    setShowPassword(false);
  }, [changePasswordDialog]);

  // Guard against null user
  if (!user) {
    return <div>{t('common.loading')}</div>;
  }

  return (
    <AppLayout
      currentPath="/profile"
      sidebar={facade.sidebar}
      user={{
        isLoggedin: !!user,
        name: user?.name,
        avatar: user?.avatar,
      }}
      userMenuProps={
        user
          ? {
              points: headerProps.points,
              pointsLabel: t('profile.pointsLabel'),
              menuItems: userMenuItems || [],
              socialLinks: userMenuSocialLinks || [],
              onRequestUpgrade,
              isRequestingUpgrade: isRequestingUpgrade || false,
              canRequestUpgrade: (headerProps.points || 0) <= 0,
              isWeb,
            }
          : undefined
      }
      subscriptionExpirationBanner={
        isExpiringWithin3Days
          ? {
              isVisible: true,
              daysUntilExpiration,
              onRenew: onRenewSubscription,
            }
          : undefined
      }
      isWeb={isWeb}
      testId="profile-sidemenu"
      className="data-testid-profile-page"
      height="screen"
      defaultSidebarCollapsed={false}
    >
      {/* Match the StartupSubPageShell top bar — sticky Header with a title,
          no back arrow (profile isn't nested under a startup). */}
      <Header
        {...headerProps}
        showBackButton
        showBackArrow={false}
        backText={t('profile.title')}
        isHasBorder
        className="sticky top-0 w-full"
        isWeb
      />

      <section className="mx-auto w-full max-w-5xl px-6 py-10" data-testid="profile-page">
        <header className="mb-6 flex flex-col gap-2">
          <h2
            className="text-2xl text-start font-semibold text-gray-800 dark:text-gray-100"
            data-testid="projects-list"
          >
            {t('profile.title')}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">{t('profile.subtitle')}</p>
        </header>
        <div className="flex flex-col bg-background dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-8 gap-8">
          {/* User Information Card */}
          <div className="flex flex-col">
            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-100">
              {t('profile.personalInfo')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {t('profile.accountDetails')}
            </p>
          </div>
          <div className="flex flex-col gap-4">
            {/* Name Field */}
            <div data-testid="profile-name-section">
              <Label
                className="text-sm text-gray-500 dark:text-gray-400"
                data-testid="profile-name-label"
              >
                {t('profile.name')}
              </Label>
              <div
                className="text-lg text-primary-800 dark:text-gray-100"
                data-testid="profile-name-value"
              >
                {user.name}
              </div>
            </div>

            {/* Email Field */}
            <div data-testid="profile-email-section">
              <Label
                className="text-sm text-gray-500 dark:text-gray-400"
                data-testid="profile-email-label"
              >
                {t('profile.email')}
              </Label>
              <div
                className="text-lg text-primary-800 dark:text-gray-100"
                data-testid="profile-email-value"
              >
                {user.email}
              </div>
            </div>

            {/* Password Section (hidden for Google OAuth) */}
            {user.provider.toLowerCase() !== 'google' && (
              <div className="flex flex-col gap-2" data-testid="profile-password-section">
                <div>
                  <Label
                    className="text-sm text-gray-500 dark:text-gray-400"
                    data-testid="profile-password-label"
                  >
                    {t('profile.password')}
                  </Label>
                  <div
                    className="text-lg text-primary-800 dark:text-gray-100"
                    data-testid="profile-password-value"
                  >
                    ************
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Appearance / Theme */}
          <div
            className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-6"
            data-testid="profile-appearance-section"
          >
            <div className="flex flex-col">
              <Label
                className="text-sm text-gray-500 dark:text-gray-400"
                data-testid="profile-theme-label"
              >
                {t('profile.appearance')}
              </Label>
              <span
                className="text-lg text-primary-800 dark:text-primary-100"
                data-testid="profile-theme-value"
              >
                {isDark ? t('profile.darkMode') : t('profile.lightMode')}
              </span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={isDark}
              aria-label={t('profile.toggleDarkMode')}
              onClick={toggleTheme}
              data-testid="btn-toggle-theme"
              className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                isDark ? 'bg-primary-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={cn(
                  'absolute top-1 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white shadow transition-[inset-inline-start] duration-200',
                  isDark ? 'start-9' : 'start-1'
                )}
              >
                {isDark ? (
                  <Moon size={14} className="text-primary-600" />
                ) : (
                  <Sun size={14} className="text-yellow-500" />
                )}
              </span>
            </button>
          </div>

          {/* Language (web-only; host injects the switcher) */}
          {languageSwitcher && (
            <div
              className="border-t border-gray-200 dark:border-gray-700 pt-6"
              data-testid="profile-language-section"
            >
              {languageSwitcher}
            </div>
          )}

          {/* Logout Button */}
          <div className="flex gap-4 mt-4">
            {user.provider.toLowerCase() !== 'google' && (
              <Button
                variant="secondary-gray"
                size="sm"
                className="text-primary-600 hover:text-primary-800 hover:bg-primary-50"
                data-testid="btn-change-password"
                onClick={changePasswordDialog.open}
              >
                {t('profile.changePassword')}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={logoutDialog.open}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-auto"
              data-testid="btn-logout"
            >
              {t('common.logout')}
            </Button>
          </div>
        </div>
      </section>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialog.isOpen} onOpenChange={logoutDialog.close}>
        <DialogContent className="w-dialog-width flex flex-col" data-testid="logout-dialog">
          <DialogHeader className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Info size={isWeb ? 22 : 18} strokeWidth={2} color="#0B4A6F" />
              <DialogTitle>{t('profile.logoutDialog.title')}</DialogTitle>
            </div>
            <button
              onClick={logoutDialog.close}
              className="dialog-close-button"
              aria-label={t('common.close')}
            >
              <X color="#667085" strokeWidth={2.5} />
            </button>
          </DialogHeader>

          <p className="dialog-description">{t('profile.logoutDialog.description')}</p>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button variant="outline" onClick={onLogout} data-testid="btn-logout-quit">
              {t('profile.logoutDialog.quit')}
            </Button>
            <Button
              variant="primary"
              onClick={logoutDialog.close}
              data-testid="btn-logout-continue"
            >
              {t('profile.logoutDialog.continue')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={changePasswordDialog.isOpen} onOpenChange={handlePasswordDialogClose}>
        <DialogContent
          className="w-dialog-width flex flex-col"
          data-testid="change-password-dialog"
        >
          <DialogHeader className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Info size={isWeb ? 22 : 18} strokeWidth={2} color="#0B4A6F" />
              <DialogTitle>{t('profile.passwordDialog.title')}</DialogTitle>
            </div>
            <button
              onClick={handlePasswordDialogClose}
              className="dialog-close-button"
              data-testid="btn-close-change-password"
              aria-label={t('common.close')}
            >
              <X color="#667085" strokeWidth={2.5} />
            </button>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="oldPassword" data-testid="old-password-label">
                {t('profile.passwordDialog.current')}
              </Label>
              <div className="relative">
                <Input
                  id="oldPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('profile.passwordDialog.current')}
                  value={oldPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setOldPassword(e.target.value);
                    setPasswordError('');
                  }}
                  data-testid="input-old-password"
                  disabled={changePasswordDialog.isChanging}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="absolute end-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={togglePassword}
                  data-testid="btn-toggle-password"
                  aria-label={
                    showPassword
                      ? t('profile.passwordDialog.hidePassword')
                      : t('profile.passwordDialog.showPassword')
                  }
                  disabled={changePasswordDialog.isChanging}
                >
                  {!showPassword ? (
                    <EyeOff size={22} strokeWidth={2.5} color="#98A2B3" />
                  ) : (
                    <Eye size={22} strokeWidth={2.5} color="#98A2B3" />
                  )}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="newPassword" data-testid="new-password-label">
                {t('profile.passwordDialog.new')}
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('profile.passwordDialog.new')}
                  value={newPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value;
                    setNewPassword(value);
                    checkPassword(value);
                  }}
                  className="pe-10"
                  data-testid="input-new-password"
                  disabled={changePasswordDialog.isChanging}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="absolute end-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={togglePassword}
                  data-testid="btn-toggle-password"
                  aria-label={
                    showPassword
                      ? t('profile.passwordDialog.hidePassword')
                      : t('profile.passwordDialog.showPassword')
                  }
                  disabled={changePasswordDialog.isChanging}
                >
                  {!showPassword ? (
                    <EyeOff size={22} strokeWidth={2.5} color="#98A2B3" />
                  ) : (
                    <Eye size={22} strokeWidth={2.5} color="#98A2B3" />
                  )}
                </Button>
              </div>

              {(isSamePassword ? samePasswordMessage : passwordError) && (
                <p className="text-base text-red-600" data-testid="password-error">
                  {isSamePassword ? samePasswordMessage : passwordError}
                </p>
              )}

              <p className="text-xs text-gray-500">{t('profile.passwordDialog.requirements')}</p>

              {/* Password Strength Indicator */}
              {newPassword && (
                <div className="space-y-1" data-testid="password-strength-indicator">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">{t('profile.passwordDialog.strength')}</span>
                    <span
                      className={`font-medium ${passwordStrength.isValid ? 'text-green-600' : 'text-orange-600'}`}
                    >
                      {passwordStrength.score < 3
                        ? t('profile.passwordDialog.weak')
                        : passwordStrength.score < 5
                          ? t('profile.passwordDialog.medium')
                          : t('profile.passwordDialog.strong')}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.className}`}
                      style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={handlePasswordDialogClose}
              data-testid="btn-cancel-change-password"
              disabled={changePasswordDialog.isChanging}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handlePasswordChange}
              disabled={
                !oldPassword ||
                isSamePassword ||
                !passwordStrength.isValid ||
                !newPassword ||
                changePasswordDialog.isChanging
              }
              data-testid="btn-submit-change-password"
            >
              {changePasswordDialog.isChanging
                ? t('profile.passwordDialog.changing')
                : t('profile.passwordDialog.change')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
